import { getLocation } from "@src/appV2/redesign/utils/routes";

import { WorkerMenuPrivileges } from "../privilegesProviders/WorkerMenuPrivilegesProvider";
import {
  DEFAULT_NAVIGATION_TRANSLATION_LABELS,
  type NavigationTranslationLabels,
} from "./translationLabels";
import { type NavigationItem, type NavigationMenu, type NavigationSection } from "./types";
import { getRouteAndSelected, isPathSelected } from "./utils";

export function getWorkerNavigationMenu(
  workerMenuPrivileges: WorkerMenuPrivileges,
  currentPath: string,
  workerId: string,
  translationLabels: NavigationTranslationLabels = DEFAULT_NAVIGATION_TRANSLATION_LABELS
): NavigationMenu {
  const items: Array<NavigationItem | NavigationSection> = [];

  if (workerMenuPrivileges.homeHealthSection) {
    if (workerMenuPrivileges.homeHealthSection.cases) {
      items.push({
        label: translationLabels.cases,
        iconType: "profile",
        ...getRouteAndSelected("agentCases", currentPath),
      });
    }
    if (workerMenuPrivileges.homeHealthSection.visits) {
      items.push({
        label: translationLabels.visitVerification,
        iconType: "badge-check",
        ...getRouteAndSelected("agentVisits", currentPath),
      });
    }
  }

  if (workerMenuPrivileges.dashboard) {
    items.push({
      label: translationLabels.calendar,
      iconType: "calendar",
      ...getRouteAndSelected("agentDashboard", currentPath),
    });
  }

  if (workerMenuPrivileges.chat) {
    items.push({
      label: translationLabels.chat,
      iconType: "message",
      ...getRouteAndSelected("chat", currentPath),
    });
  }

  if (workerMenuPrivileges.dnr) {
    items.push({
      label: translationLabels.dnr,
      iconType: "gps-disabled",
      ...getRouteAndSelected("agentDnr", currentPath),
    });
  }

  if (workerMenuPrivileges.payrollSection) {
    const sectionItems: NavigationItem[] = [];

    if (workerMenuPrivileges.payrollSection.payroll) {
      sectionItems.push({
        label: translationLabels.payroll,
        iconType: "money-dollar",
        ...getRouteAndSelected("agentPayroll", currentPath),
      });
    }
    if (workerMenuPrivileges.payrollSection.stripeTransfers) {
      sectionItems.push({
        label: translationLabels.stripeTransfers,
        iconType: "dollar",
        ...getRouteAndSelected("agentStripeTransfers", currentPath),
      });
    }
    if (workerMenuPrivileges.payrollSection.stripePayouts) {
      sectionItems.push({
        label: translationLabels.stripePayouts,
        iconType: "palm-dollar",
        ...getRouteAndSelected("agentStripePayouts", currentPath),
      });
    }

    items.push({ label: translationLabels.payroll, items: sectionItems });
  }

  if (workerMenuPrivileges.clipboardScore) {
    const sectionItems: NavigationItem[] = [];

    sectionItems.push({
      label: translationLabels.clipboardScore,
      iconType: "notepad-alt",
      ...getRouteAndSelected("agentAttendance", currentPath),
    });

    items.push({ label: translationLabels.clipboardScore, items: sectionItems });
  }

  if (workerMenuPrivileges.placementSection) {
    const sectionItems: NavigationItem[] = [];

    if (workerMenuPrivileges.placementSection.candidateProfile) {
      sectionItems.push({
        label: translationLabels.candidateProfile,
        iconType: "identity-card",
        ...getRouteAndSelected("agentPlacement", currentPath),
      });
    }
    if (workerMenuPrivileges.placementSection.exclusions) {
      sectionItems.push({
        label: translationLabels.exclusions,
        iconType: "folder-person",
        ...getRouteAndSelected("agentPlacementCandidateExclusions", currentPath),
      });
    }
    if (workerMenuPrivileges.placementSection.documents) {
      const documentsRouteTo = getLocation("document", { pathParams: { userId: workerId } });
      sectionItems.push({
        label: translationLabels.documents,
        iconType: "documents",
        routeTo: documentsRouteTo,
        isSelected: documentsRouteTo.pathname
          ? isPathSelected(currentPath, documentsRouteTo.pathname)
          : false,
      });
    }

    items.push({ label: translationLabels.placement, items: sectionItems });
  }

  return { items };
}
